<template>
    <DialogLayout :class-name="'confirm-join-dialog'" @close="handleClose">
        <template #title>{{t('candidate.confirmJoin')}}</template>
        <template #default>
            <el-form ref="formRef" :model="formData" :rules="rules" :label-position="'left'" :label-width="'100px'"
                class="confirm-join-form">
                <el-form-item :label="t('candidate.isJoined')" prop="joined">
                    <el-radio-group v-model="formData.joined" :disabled="props.disabledRadio">
                        <el-radio :value="1">{{t('candidate.joined')}}</el-radio>
                        <el-radio :value="2">{{t('candidate.notJoined')}}</el-radio>
                    </el-radio-group>
                </el-form-item>

                <el-form-item :label="t('candidate.remarkInfo')" prop="reason">
                    <el-input v-model="formData.reason" type="textarea" :placeholder="t('common.pleaseEnter')" :rows="4" maxlength="200"
                        show-word-limit />
                </el-form-item>
            </el-form>
        </template>
        <template #footer>
            <el-button class="confirm-button-default" @click="handleCancel">
                {{ t('common.cancel') }}
            </el-button>
            <el-button type="primary" :loading="submitting" @click="handleConfirm">
                {{ confirmButtonText }}
            </el-button>
        </template>
    </DialogLayout>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, type FormInstance, type FormRules } from 'element-plus'
import DialogLayout from './dialog-layout.vue'
import type { ConfirmJoinData, ConfirmJoinDialogProps } from './confirm-join-dialog-types'
import { confirmJoinApi, updateInterviewEvaluationApi } from '@/api/candidate'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const props = defineProps<ConfirmJoinDialogProps>()

const emit = defineEmits<{
    (e: 'confirm', data: ConfirmJoinData): void
    (e: 'cancel'): void
    (e: 'close'): void
}>()

const formRef = ref<FormInstance>()
const submitting = ref(false)

// 按钮文本
const confirmButtonText = computed(() => {
    return props.data ? t('common.save') : t('common.confirm1')
})

// 表单数据
const formData = reactive<ConfirmJoinData>({
    joined: undefined,
    reason: ''
})

// 表单验证规则（动态规则）
const rules = computed<FormRules>(() => {
    const baseRules: FormRules = {
    }

    return baseRules
})


// 初始化表单数据
onMounted(() => {
    if (props.data) {
        formData.joined = props.data.joined
        formData.reason = props.data.reason || ''
    } else {
        formData.joined = 1
        formData.reason = ''
    }
})

// 确认
const handleConfirm = async () => {
    if (!formRef.value) return

    try {
        // 先验证表单，如果验证失败会抛出错误，不会继续执行
        await formRef.value.validate()
    } catch (error: unknown) {
        // 表单验证失败，不显示额外提示（Element Plus 会自动显示），直接返回，不提交接口
        if (error && typeof error === 'object' && 'fields' in error) {
            return
        }
        // 其他验证错误也直接返回
        return
    }

    // 验证通过后，才执行 API 调用
    try {
        submitting.value = true

        // 调用 API 确认入职
        const submitApi = props.data ? updateInterviewEvaluationApi : confirmJoinApi
        await submitApi({
            interviewId: props.interviewId,
            stepId: props.stepId,
            joined: formData.joined!,
            reason: formData.reason || '',
            comments: formData.reason || ''
        })

        // API 调用成功
        const resultData: ConfirmJoinData = {
            joined: formData.joined,
            reason: formData.reason
        }
        
        ElMessage.success(t('common.saveSuccess'))
        submitting.value = false
        emit('confirm', resultData)
    } catch (error: unknown) {
        submitting.value = false
        
        // API 调用失败
        const errorMessage = error && typeof error === 'object' && 'message' in error 
            ? String(error.message) 
            : t('common.saveFailed')
        console.error('提交失败:', error)
        ElMessage.error(errorMessage)
    }
}

// 取消
const handleCancel = () => {
    emit('cancel')
}

// 关闭
const handleClose = () => {
    emit('close')
}
</script>

<style scoped lang="scss">
.confirm-join-form {


    :deep(.el-form-item__label) {
        font-size: 14px;
        font-weight: 500;
        color: #212429;
        line-height: 35px;
        text-align: right;
        padding-right: 12px;
        margin-bottom: 0px;

        &::before {
            display: none;
        }
    }

    :deep(.el-radio-group) {
        display: flex;
        gap: 20px;
    }

    :deep(.el-radio) {
        margin-right: 0;

        .el-radio__label {
            font-size: 14px;
            color: #495057;
            padding-left: 8px;
        }
    }

    :deep(.el-textarea) {
        .el-textarea__inner {
            font-size: 14px;
            color: #495057;
            line-height: 22px;
            resize: none;

            &::placeholder {
                color: #C9D3D9;
            }
        }
    }

    :deep(.el-input__count) {
        font-size: 12px;
        color: #86909C;
        background: transparent;
    }
}

:deep(.confirm-join-dialog) {
    width: 512px;

    
}
</style>

<style lang="scss">
.confirm-join-dialog {
    width: 512px !important;
    .dialog-footer {
            display: flex;
            justify-content: flex-end;
            gap: 12px;
    
            .el-button {
                min-width: 80px;
            }
        }
}
</style>

